import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "../components/Loader";
import {
  DeliveredToAll,
  getChat,
  pushMoreChat,
  SeenByAll,
  syncChat,
  syncChatMessages,
} from "../localDb/personal_chat";
import Api from "../service/http";
import { useSocket } from "../service/SocketProvider";
import Details from "./chat/Details";
import Messages from "./chat/Messages";

const PersonalChat = () => {
  const chat_id = useParams().id;
  const chatId = useRef();
  const { socketSend } = useSocket();

  const user = JSON.parse(localStorage.getItem("user"));

  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(false);

  const getChatFromServer = async (isScroll = true) => {
    const chatData = getChat(chatId?.current);
    setChat(chatData);

    if (isScroll) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      scrollToBottom(false);
    }

    try {
      const response = await Api.get("/web/chat/" + chatId?.current, {
        params: {
          min_id: chatData?.first_message_id ?? null,
        },
      });
      if (response.data?.data) {
        if (String(chatId?.current) === String(response.data?.data?.id)) {
          syncChat(response.data?.data);
          setChat(response.data?.data);
        }

        if (user?.id) {
          socketSend({
            type: "ReadByAll",
            user_id: user?.id,
            chat_id: response.data?.data?.id,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching chat from server:", error);
    }
  };

  useEffect(() => {
    if (chat_id) {
      chatId.current = chat_id;
      getChatFromServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when route id changes
  }, [chat_id]);

  const loadMoreFromServer = async () => {
    if (loading) return;
    setLoading(true);
    const prevFirstMessageId = chat?.first_message_id;

    const response = await Api.get("/web/chat/" + chat?.id + "/more", {
      params: {
        min_id: prevFirstMessageId ?? null,
      },
    });

    if (response.data?.data) {
      setChat(pushMoreChat(response.data?.data));

      setTimeout(() => {
        const el = document.getElementById(`message-${prevFirstMessageId}`);
        if (el) {
          el.scrollIntoView({ behavior: "instant", block: "start" });
          const chatElement = document.querySelector(".chat-list");
          if (chatElement) {
            chatElement.scrollTop = el.offsetTop - 150;
          }
        }
      }, 1);
    }
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const checkScrollPosition = () => {
    const chatElement = document.querySelector(".chat-list");
    if (chatElement) {
      const scrollTop = chatElement.scrollTop;
      const scrollHeight = chatElement.scrollHeight;
      const clientHeight = chatElement.clientHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
      const isAtTop = scrollTop <= 5;

      if (isAtBottom) {
        return "bottom";
      }
      if (isAtTop) {
        return "top";
      }
      return "middle";
    }
  };

  const scrollToBottom = (behavior = true) => {
    const chatElement = document.querySelector(".chat-list");
    chatElement?.scrollTo({
      top: chatElement.scrollHeight,
      behavior: behavior ? "smooth" : "auto",
    });
  };

  const { socket, subscribe, unsubscribe } = useSocket();

  const [typingUser, setTypingUser] = useState([]);
  const controlTyping = (chat_id, typeUser, typing) => {
    if (String(chat_id) !== String(chatId?.current)) return;
    if (typeUser?.id === user?.id) return;

    const now = new Date().toISOString();

    if (typing) {
      setTypingUser((prevTypingUsers) => {
        const existingIndex = prevTypingUsers.findIndex(
          (item) => item?.id === typeUser?.id
        );

        if (existingIndex !== -1) {
          const updatedUsers = [...prevTypingUsers];
          updatedUsers[existingIndex] = {
            ...updatedUsers[existingIndex],
            lastTypedAt: now,
          };
          return updatedUsers;
        }
        return [...prevTypingUsers, { ...typeUser, lastTypedAt: now }];
      });

      if (checkScrollPosition() === "bottom") {
        scrollToBottom();
      }
    } else {
      setTypingUser((prevTypingUsers) =>
        prevTypingUsers?.filter((item) => item?.id !== typeUser?.id)
      );
    }
  };
  useEffect(() => {
    const interval = setInterval(() => {
      setTypingUser((prevTypingUsers) => {
        const now = Date.now();
        return prevTypingUsers.filter(
          (u) =>
            u.lastTypedAt &&
            new Date(u.lastTypedAt).getTime() > now - 5000
        );
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMessage = (data) => {
      switch (data.type) {
        case "WebMeesageTyping":
          controlTyping(data.chat_id, data?.user, data.typing);
          break;
        case "WebFetchMessage":
          if (String(data?.message?.chat_id) === String(chatId?.current)) {
            syncChatMessages(data?.message?.chat_id, data?.message);
            setChat(getChat(chatId?.current));
            if (data?.isScroll) {
              scrollToBottom(data?.isScroll);
            }
          if (user?.id) {
            socketSend({
              type: "ReadByAll",
              user_id: user?.id,
              chat_id: data?.message?.chat_id,
            });
          }
          }
          break;
        case "WebFetchChat":
          if (String(data?.message?.chat_id) === String(chatId?.current)) {
            getChatFromServer(data?.isScroll);
          }
          break;
        case "DeliveredToAll":
          DeliveredToAll(data?.chats, user?.id);
          if (data?.chats?.some((c) => String(c?.id) === String(chatId?.current))) {
            setChat(getChat(chatId?.current));
          }
          break;
        case "ReadByAll":
          SeenByAll(data?.chat, user?.id);
          if (String(data?.chat?.id) === String(chatId?.current)) {
            setChat(getChat(chatId?.current));
          }
          break;
        default:
          break;
      }
    };

    if (socket) subscribe(handleMessage);
    return () => unsubscribe(handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- socket handlers mirror group Chat.js
  }, [socket, subscribe, unsubscribe]);

  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {chat ? (
        <div className="flex h-full w-full relative">
          <Messages
            chat={chat}
            setChat={setChat}
            messages={chat?.messages}
            typingUser={typingUser}
            scrollToBottom={scrollToBottom}
            loadMoreFromServer={loadMoreFromServer}
            moreLoading={loading}
            showDetails={showDetails}
            setShowDetails={setShowDetails}
          />
          <Details
            chat={chat}
            setChat={setChat}
            showDetails={showDetails}
            setShowDetails={setShowDetails}
          />
        </div>
      ) : (
        <Loader className="m-auto" />
      )}
    </div>
  );
};

export default PersonalChat;
